import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import usePagedRows from "../components/usePagedRows";
import Pagination from "../components/Pagination";
import { Badge } from "../components/Badge";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  RefreshCw,
  Search,
  Filter,
  Wallet,
  TrendingUp,
  Users,
  XCircle,
  Loader2,
  FileDown,
} from "lucide-react";
import { toast } from "sonner";

const fmtBdt = (n) => `৳${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function AgentTransactions({
  presetType = "",
  presetStatus = "",
  title = "Agent Transactions",
  subtitle = "Full audit trail of every user ↔ agent deposit and withdrawal across the platform.",
  testId = "admin-agent-transactions-page",
} = {}) {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: presetType || "",
    agent_id: "",
    user_query: "",
    status: presetStatus || "",
    date_from: "",
    date_to: "",
  });
  const paged = usePagedRows(rows, 12, [filters.type, filters.agent_id, filters.user_query, filters.status, filters.date_from, filters.date_to]);
  const typeLocked = !!presetType;
  const statusLocked = !!presetStatus;

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      params.set("limit", "500");
      const { data } = await api.get(`/agent-transactions?${params.toString()}`);
      setRows(data?.items || []);
      setStats(data?.stats || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/agent-transactions/agents").then(({ data }) => setAgents(data?.items || [])).catch(() => {});
    // Pre-fill filters from URL query params (e.g., ?agent_id=...&date_from=...&date_to=...)
    try {
      const sp = new URLSearchParams(window.location.search);
      const next = { ...filters };
      let any = false;
      ["type", "agent_id", "user_query", "status", "date_from", "date_to"].forEach((k) => {
        // Don't override locked preset fields from URL
        if ((k === "type" && typeLocked) || (k === "status" && statusLocked)) return;
        const v = sp.get(k);
        if (v) { next[k] = v; any = true; }
      });
      if (any) {
        setFilters(next);
        // load with overrides
        (async () => {
          setLoading(true);
          try {
            const p = new URLSearchParams();
            Object.entries(next).forEach(([k, v]) => v && p.set(k, v));
            p.set("limit", "500");
            const { data } = await api.get(`/agent-transactions?${p.toString()}`);
            setRows(data?.items || []);
            setStats(data?.stats || {});
          } finally { setLoading(false); }
        })();
        return;
      }
    } catch (_e) {}
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const submitCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const { data } = await api.post(
        `/agent-transactions/withdrawal/${cancelTarget.id}/cancel`,
        { reason: cancelReason || "Cancelled by admin" }
      );
      toast.success(
        `Withdrawal cancelled · Refunded ৳${Number(data.amount).toLocaleString()} to user${
          data.deducted_from_agent ? ` & deducted from ${cancelTarget.agent_username}` : ""
        }`
      );
      setCancelTarget(null);
      setCancelReason("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to cancel withdrawal");
    } finally {
      setCancelling(false);
    }
  };

  const reset = () => {
    setFilters({
      type: presetType || "",
      agent_id: "",
      user_query: "",
      status: presetStatus || "",
      date_from: "",
      date_to: "",
    });
    setTimeout(load, 100);
  };

  const [downloading, setDownloading] = useState(false);
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      params.set("limit", "1000");
      const { data } = await api.get(
        `/agent-transactions/download.pdf?${params.toString()}`,
        { responseType: "blob" },
      );
      const ts = new Date().toISOString().slice(0, 16).replace("T", "-").replace(":", "");
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const slug = [
        presetType === "deposit" ? "agent-deposits" :
        presetType === "withdrawal" ? "agent-withdrawals" : "agent-transactions",
        presetStatus ? presetStatus : "",
      ].filter(Boolean).join("-");
      a.download = `${slug}-${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const summary = useMemo(() => ([
    { label: "Total Records", value: stats.count || 0, icon: TrendingUp, tone: "emerald" },
    { label: "Agent Deposits", value: stats.deposit_count || 0, sub: fmtBdt(stats.total_deposits || 0), icon: ArrowDownToLine, tone: "sky" },
    { label: "Agent Withdrawals", value: stats.withdrawal_count || 0, sub: fmtBdt(stats.total_withdrawals || 0), icon: ArrowUpFromLine, tone: "violet" },
    { label: "Agents Commission", value: fmtBdt(stats.total_commission || 0), sub: "earned", icon: Wallet, tone: "amber" },
  ]), [stats]);

  return (
    <div data-testid={testId}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={downloadPdf}
              disabled={downloading}
              className="h-9 px-3 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-sm text-[13px] font-semibold"
              data-testid="agent-tx-download-pdf"
            >
              {downloading
                ? <Loader2 size={13} className="animate-spin" />
                : <FileDown size={13} />} Download PDF
            </button>
            <button
              onClick={load}
              className="h-9 px-3 inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-emerald-300 rounded-sm text-[13px]"
              data-testid="agent-tx-refresh"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        }
      />

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {summary.map((c) => (
          <SummaryCard key={c.label} {...c} />
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-sm p-3 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={13} className="text-gray-500" />
          <div className="text-[12px] uppercase tracking-wider text-gray-500 font-semibold">Filters</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {!typeLocked && (
            <SelectField label="Type" value={filters.type} onChange={handleFilterChange("type")} testid="filter-type">
              <option value="">All types</option>
              <option value="deposit">Agent → User Deposit</option>
              <option value="withdrawal">User → Agent Withdrawal</option>
            </SelectField>
          )}
          <SelectField label="Agent" value={filters.agent_id} onChange={handleFilterChange("agent_id")} testid="filter-agent">
            <option value="">All agents</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>{a.username} — {a.name}</option>
            ))}
          </SelectField>
          {!statusLocked && (
            <SelectField label="Status" value={filters.status} onChange={handleFilterChange("status")} testid="filter-status">
              <option value="">All status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </SelectField>
          )}
          <InputField label="User (username / player ID / name)" value={filters.user_query} onChange={handleFilterChange("user_query")} testid="filter-user" placeholder="e.g. demo_user or 302297" />
          <InputField label="From date" type="date" value={filters.date_from} onChange={handleFilterChange("date_from")} testid="filter-from" />
          <InputField label="To date" type="date" value={filters.date_to} onChange={handleFilterChange("date_to")} testid="filter-to" />
        </div>
        <div className="flex items-center justify-end gap-2 mt-3">
          <button onClick={reset} className="h-8 px-3 border border-gray-200 rounded-sm text-[12px] hover:bg-gray-50" data-testid="filter-reset">Reset</button>
          <button onClick={load} className="h-8 px-3 inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-[12px] font-semibold" data-testid="filter-apply">
            <Search size={12} /> Apply filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="agent-tx-table">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left py-2.5 px-3 font-semibold">Date</th>
                <th className="text-left py-2.5 px-3 font-semibold">Type</th>
                <th className="text-left py-2.5 px-3 font-semibold"><Users size={12} className="inline mr-1" />User</th>
                <th className="text-left py-2.5 px-3 font-semibold">Agent</th>
                <th className="text-right py-2.5 px-3 font-semibold">Amount</th>
                <th className="text-right py-2.5 px-3 font-semibold">Commission</th>
                <th className="text-left py-2.5 px-3 font-semibold">Note / Code</th>
                <th className="text-left py-2.5 px-3 font-semibold">Status</th>
                <th className="text-right py-2.5 px-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="py-10 text-center text-gray-400">Loading transactions...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="py-10 text-center text-gray-400">No transactions found. Try clearing filters.</td></tr>
              ) : (
                paged.visible.map((r) => (
                  <tr key={`${r.type}-${r.id}`} className="border-t border-gray-100 hover:bg-gray-50/50" data-testid={`agent-tx-row-${r.id}`}>
                    <td className="py-2.5 px-3 whitespace-nowrap text-gray-600">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="py-2.5 px-3">
                      {r.type === "deposit" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-sky-300 bg-sky-50 text-sky-700 text-[11px] font-bold">
                          <ArrowDownToLine size={11} /> Deposit
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-violet-300 bg-violet-50 text-violet-700 text-[11px] font-bold">
                          <ArrowUpFromLine size={11} /> Withdrawal
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="font-bold text-gray-900">{r.player_name || r.username}</div>
                      <div className="text-[11px] text-gray-500 font-mono">@{r.username} · ID {r.player_id}</div>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="font-bold text-emerald-700 font-mono">{r.agent_username}</div>
                      <div className="text-[11px] text-gray-500">{r.agent_name}</div>
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono font-bold tabular-nums">{fmtBdt(r.amount)}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-amber-700 tabular-nums">
                      {r.commission_amount > 0 ? (
                        <span title={`${r.commission_pct}%`}>{fmtBdt(r.commission_amount)}</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-gray-600 max-w-[220px] truncate" title={r.note || r.code || ""}>
                      {r.type === "withdrawal" && r.code ? (
                        <span className="font-mono text-[11px] bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5 rounded">{r.code}</span>
                      ) : (r.note || <span className="text-gray-300">—</span>)}
                    </td>
                    <td className="py-2.5 px-3">
                      <Badge
                        variant={
                          r.status === "completed" || r.status === "approved" ? "success"
                            : r.status === "rejected" || r.status === "failed" || r.status === "cancelled" ? "danger"
                            : "warning"
                        }
                      >
                        {r.status}
                      </Badge>
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      {r.type === "withdrawal" && r.status !== "cancelled" && r.status !== "rejected" ? (
                        <button
                          onClick={() => { setCancelTarget(r); setCancelReason(""); }}
                          data-testid={`cancel-withdrawal-btn-${r.id}`}
                          className="inline-flex items-center gap-1 h-7 px-2 border border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-sm text-[11px] font-bold transition"
                          title={r.status === "approved" ? "Cancel approved withdrawal — will deduct from agent balance" : "Cancel pending withdrawal — will refund user"}
                        >
                          <XCircle size={11} /> Cancel
                        </button>
                      ) : (
                        <span className="text-gray-300 text-[11px]">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-3 py-2 border-t border-gray-100 text-[11px] text-gray-500 flex items-center justify-between">
          <span>{rows.length} record{rows.length !== 1 ? "s" : ""}</span>
          <span>Showing combined deposits & withdrawals · sorted by date desc</span>
        </div>
        <Pagination total={paged.totalRows} page={paged.page} pageSize={paged.pageSize} onChange={paged.setPage} testIdPrefix="agent-tx-pg" />
      </div>

      {/* Cancel withdrawal modal */}
      {cancelTarget && (
        <div
          onClick={() => !cancelling && setCancelTarget(null)}
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4"
          data-testid="cancel-withdrawal-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-md max-w-md w-full p-5 border border-gray-200 shadow-2xl"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="h-10 w-10 rounded-md grid place-items-center bg-rose-100 text-rose-600 flex-shrink-0">
                <XCircle size={20} />
              </div>
              <div className="min-w-0">
                <h3 className="text-[15px] font-bold text-gray-900">Cancel withdrawal?</h3>
                <p className="text-[12.5px] text-gray-500 mt-0.5">
                  {cancelTarget.status === "approved"
                    ? "This withdrawal was already approved. Cancelling will refund the user AND deduct the same amount from the agent's balance."
                    : "This will refund the full amount back to the user's wallet immediately."}
                </p>
              </div>
            </div>

            <div className="rounded-sm border border-gray-200 bg-gray-50 p-3 text-[12px] space-y-1 mb-4">
              <Row label="User" value={`${cancelTarget.player_name || cancelTarget.username} (@${cancelTarget.username})`} />
              <Row label="Agent" value={`${cancelTarget.agent_username} — ${cancelTarget.agent_name}`} />
              <Row label="Amount" value={fmtBdt(cancelTarget.amount)} mono />
              <Row label="Code" value={cancelTarget.code || "—"} mono />
              <Row label="Current status" value={cancelTarget.status} />
            </div>

            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-1">Reason (optional, sent to audit log)</label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. User requested by mistake"
              rows={2}
              className="w-full px-2 py-1.5 border border-gray-200 rounded-sm text-[13px] focus:border-emerald-400 focus:outline-none resize-none"
              data-testid="cancel-withdrawal-reason"
            />

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                onClick={() => setCancelTarget(null)}
                disabled={cancelling}
                className="h-8 px-3 border border-gray-200 rounded-sm text-[12.5px] font-semibold hover:bg-gray-50 disabled:opacity-50"
                data-testid="cancel-withdrawal-dismiss"
              >
                Keep withdrawal
              </button>
              <button
                onClick={submitCancel}
                disabled={cancelling}
                className="h-8 px-3 bg-rose-600 hover:bg-rose-700 text-white rounded-sm text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="cancel-withdrawal-confirm"
              >
                {cancelling ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                {cancelling ? "Cancelling…" : "Yes, cancel & refund"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-gray-500">{label}</span>
      <span className={`text-gray-900 font-semibold ${mono ? "font-mono" : ""} truncate`}>{value}</span>
    </div>
  );
}

function SummaryCard({ label, value, sub, icon: Icon, tone }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50/50",
    sky: "border-sky-200 bg-sky-50/50",
    violet: "border-violet-200 bg-violet-50/50",
    amber: "border-amber-200 bg-amber-50/50",
  };
  const iconTones = {
    emerald: "text-emerald-600 bg-emerald-100",
    sky: "text-sky-600 bg-sky-100",
    violet: "text-violet-600 bg-violet-100",
    amber: "text-amber-600 bg-amber-100",
  };
  return (
    <div className={`rounded-sm p-3.5 border ${tones[tone]}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`h-7 w-7 rounded-sm grid place-items-center ${iconTones[tone]}`}>
          <Icon size={14} />
        </div>
        <div className="text-[10.5px] uppercase tracking-wider text-gray-600 font-semibold">{label}</div>
      </div>
      <div className="text-xl font-bold tabular-nums text-gray-900">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function SelectField({ label, value, onChange, children, testid }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</span>
      <select value={value} onChange={onChange} data-testid={testid} className="w-full h-9 px-2 border border-gray-200 rounded-sm text-[12.5px] bg-white focus:border-emerald-400 focus:outline-none">
        {children}
      </select>
    </label>
  );
}

function InputField({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</span>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} data-testid={testid} className="w-full h-9 px-2 border border-gray-200 rounded-sm text-[12.5px] focus:border-emerald-400 focus:outline-none" />
    </label>
  );
}
